import React from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import metalFabrication from '../assets/services/metal-fabrication-&-Welding.jpeg'
import cnsprecision from "../assets/services/cnc-precision-machining.jpeg";
import turnkey from "../assets/services/turnkey-manufacturing-&-assembly.jpeg";
import warehousing from "../assets/services/warehousing-&-supply-chain-solutions.jpeg";
import repair from "../assets/services/repair-&-refurbishment.jpeg";
import testing from "../assets/services/hydro-static-testing-nde.jpg";
import development from "../assets/services/new-product-development-&-prototyping.jpeg";
import anniversary50 from "../assets/50_years.png";
import cuttingTool from "../assets/cutting_tool_engineering.png";

const ServiceDetail = () => {
  const { service } = useParams();

  const serviceData = {
    'metal-fabrication': {
      title: 'Metal Fabrication & Welding',
      description: 'Expert structural welding and custom metal fabrication for industrial applications with precision and quality.',
      capabilities: [
        'Structural Steel Fabrication',
        'Custom Sheet Metal Fabrication',
        'TIG, MIG, and Stick Welding',
        'Stainless Steel Fabrication',
        'Aluminum Fabrication',
        'Assembly and Sub-Assembly',
        'Laser and Plasma Cutting',
        'Metal Forming and Bending',
      ],
      imagelink: metalFabrication,
      details: 'Our metal fabrication services combine decades of welding expertise with state-of-the-art equipment to deliver precision structural fabrication. From custom steel structures to complex weldments, our certified welders ensure every project meets the highest quality standards.',
      quotesData:
      {
        quote: "After the game, the king and the pawn go into the same box.",
        author: "Italian Proverb"
      }
    },
    'turnkey-manufacturing': {
      title: 'Turnkey Manufacturing & Assembly',
      description: 'Complete end-to-end manufacturing solutions from initial design through final delivery.',
      capabilities: [
        'Design Engineering Support',
        'Rapid Prototyping',
        'Production Planning',
        'Manufacturing Execution',
        'Quality Control & Testing',
        'Final Assembly',
        'Packaging & Shipping',
        'Project Management',
      ],
      imagelink: turnkey,
      details: 'Our turnkey manufacturing service provides a single-source solution for your complete project needs. We manage every aspect from design consultation and prototyping through production, assembly, and delivery, ensuring seamless execution and on-time completion.',
      quotesData:
      {
        quote: "Simplicity is the soul of efficiency.",
        author: "Richard Austin Freeman"
      }
    },
    'cnc-machining': {
      title: 'CNC Precision Machining',
      description: 'State-of-the-art CNC machining capabilities for tight tolerance precision components.',
      capabilities: [
        '3, 4, and 5-Axis CNC Milling',
        'CNC Turning and Lathe Work',
        'Wire and Ram EDM',
        'Surface and Cylindrical Grinding',
        'Tight Tolerance Machining (±0.0005")',
        'Prototype to Production Runs',
        'Material Expertise (Steel, Aluminum, Exotic Alloys)',
        'CMM Inspection',
      ],
      imagelink: cnsprecision,
      details: 'Our precision CNC machining capabilities deliver components with exceptional accuracy and repeatability. With multi-axis machining centers and skilled machinists, we handle complex geometries and tight tolerances for critical applications.',
      quotesData:
      {
        quote: "The greatest pleasure I know is to do a good action by stealth, and to have it found out by accident.",
        author: "Charles Lamb"
      }
    },
    'warehousing': {
      title: 'Warehousing & Supply Chain Solutions',
      description: 'Comprehensive logistics and inventory management to streamline your supply chain.',
      capabilities: [
        'Inventory Management Systems',
        'Vendor Managed Inventory (VMI)',
        'Just-In-Time (JIT) Delivery',
        'Kitting and Sub-Assembly',
        'Vendor Consolidation',
        'Packaging Solutions',
        'Order Fulfillment',
        'Supply Chain Optimization',
      ],
      imagelink: warehousing,
      details: 'Reduce overhead and improve efficiency with our warehousing and supply chain solutions. We manage inventory, coordinate deliveries, and provide just-in-time fulfillment to keep your operations running smoothly.',
      quotesData:
      {
        quote: "Drawing on my fine command of speech, I said nothing.",
        author: "Robert Benchley"
      }
    },
    'repair': {
      title: 'Repair & Refurbishment',
      description: 'Expert repair services to extend the life and performance of your critical equipment.',
      capabilities: [
        'Equipment Diagnostics',
        'Component Repair',
        'Refurbishment Services',
        'Preventive Maintenance',
        'Emergency Repair Services',
        'Field Service Available',
        'OEM and Non-OEM Equipment',
        'Warranty Programs',
      ],
      imagelink: repair,
      details: 'Our repair and refurbishment services help extend equipment life and reduce replacement costs. From routine maintenance to emergency repairs, our experienced technicians restore equipment to optimal performance.',
      quotesData:
      {
        quote: "Things don’t change. You change your way of looking, that’s all.",
        author: "Carlos Castaneda"
      }
    },
    'testing': {
      title: 'Hydro Static Testing & NDE',
      description: 'Comprehensive testing and non-destructive examination services for quality assurance.',
      capabilities: [
        'Hydrostatic Pressure Testing',
        'Ultrasonic Testing (UT)',
        'Radiographic Testing (RT)',
        'Magnetic Particle Testing (MT)',
        'Liquid Penetrant Testing (PT)',
        'Visual Inspection (VT)',
        'API Certified Testing',
        'Detailed Test Reports',
      ],
      imagelink: testing,
      details: 'Ensure the integrity and safety of your equipment with our comprehensive testing services. Our certified technicians perform hydrostatic testing and various non-destructive examination methods to meet industry standards.',
      quotesData:
      {
        quote: "I like the dreams of the future better than the history of the past.",
        author: "Thomas Jefferson"
      }
    },
    'development': {
      title: 'New Product Development & Prototyping',
      description: 'From concept to reality - complete product development services.',
      capabilities: [
        'Concept Development',
        'Design Engineering',
        'CAD/CAM Modeling',
        'Rapid Prototyping',
        'Material Selection',
        'Design for Manufacturing (DFM)',
        'Testing & Validation',
        'Production Transition',
      ],
      imagelink: development,
      details: 'Transform your ideas into reality with our product development services. We work closely with you from initial concept through prototype development and testing, ensuring your product is optimized for manufacturing and market success.',
      quotesData:
      {
        quote: "You become responsible forever for what you have tamed.",
        author: "Antoine de Saint-Exupéry"
      }
    },
  };

  const currentService = serviceData[service] || serviceData['metal-fabrication'];

  return (
    <div className="min-h-screen pt-20" data-testid="service-detail-page">
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden h-[30rem]" data-testid="service-detail-hero">
        <div className="absolute inset-0">
          <img
            src={currentService.imagelink}
            alt={currentService.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10 pt-24">
          <h1 className="text-4xl sm:text-5xl font-bold text-white heading-font mb-6" data-testid="service-detail-heading">
            {currentService.title}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            {currentService.description}
          </p>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="service-detail-content">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 heading-font mb-6">Overview</h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                {currentService.details}
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-semibold transition-all shadow-md"
                data-testid="service-detail-cta"
              >
                Request a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 heading-font mb-6">Capabilities</h2>
              <ul className="space-y-3">
                {currentService.capabilities.map((capability, index) => (
                  <li key={index} className="flex items-start space-x-3" data-testid={`capability-${index}`}>
                    <CheckCircle2 className="w-6 h-6 text-blue-700 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{capability}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="service-detail-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 heading-font mb-6">Ready to Get Started?</h2>
          <p className="text-gray-600 mb-8">
            Contact us today to discuss your specific needs and learn how we can help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-semibold transition-all"
              data-testid="service-detail-contact-button"
            >
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-700 rounded-lg hover:bg-gray-50 font-semibold transition-all border-2 border-blue-700"
              data-testid="service-detail-back-button"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>
      <section className="bg-blue-800 text-white py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
          {/* LEFT SIDE */}
          <div>
            <div className="flex items-center space-x-4 mb-6">
              <img
                src={cuttingTool}
                alt="Cutting Tool Engineering"
                className="w-40"
              />

              <div>
                <h2 className="text-2xl font-bold text-blue-400 tracking-wide">
                  ARTICLES
                </h2>
                <p className="text-sm italic text-gray-200">
                  Keith Jennings, President Crow Corp.
                </p>
              </div>
            </div>

            <ul className="space-y-4 mt-8">
              {[
                "Protecting digital assets",
                "Discounts and increases",
                "Adapting to today’s customer",
                "Cultivating the future of machining",
                "IMTS was a worthy experience",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-center border-b border-blue-400/30 pb-3"
                >
                  <span className="w-3 h-3 border-2 border-blue-400 rounded-full mr-4"></span>
                  <span className="text-gray-200">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="border-l border-white/30 pl-12 flex flex-col items-center text-center">

            <p className="text-blue-300 italic text-xl mb-3">
              "{currentService.quotesData.quote}"
            </p>

            <p className="text-gray-300 mb-8">
              - {currentService.quotesData.author}
            </p>

            <img
              src={anniversary50}
              alt="50 Years"
              className="w-72 opacity-40"
            />

          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;